import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import * as XLSX from "xlsx";
import {
  Ic,
  DomainTag,
  StatusBadge,
  Modal,
  SearchableSelect,
  AutocompleteTextInput,
  inputStyle,
  btnPrimary,
  btnSecondary,
  btnGhost,
  DAYS_HEB,
  DOMAIN_COLORS,
  fmtDate,
  showStyledAlert,
  showStyledConfirm,
} from "../components/UI";
import {
  addActivity,
  updateActivity,
  deleteActivity,
  addRecurringActivity,
  updateRecurringActivity,
  addDomain,
  updateDomain,
  updateUser,
  deleteUser,
  addUser,
  syncUserVolunteer,
  addBeneficiary,
  updateBeneficiary,
  deleteBeneficiary,
} from "../utils/firestore";
import { getPrimaryCoordinatorId, getPrimaryTeamLeadId } from "../utils/volunteerAssignments";
import {
  getActivityContributionSummary,
  getActivityContributionTotal,
  getActivityContributionValueTotal,
  formatContributionCurrency,
  normalizeActivityContributions,
  sumActivitiesContributionTotal,
  sumActivitiesContributionValueTotal,
} from "../utils/activityContributions";

/* ═══════════════════════════════════════════
   HELPERS  (logic unchanged)
   ═══════════════════════════════════════════ */

function exportToXlsx(rows, sheetName, fileName) {
  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  XLSX.writeFile(wb, fileName);
}

function toHebrewDayLetters(num) {
  if (!Number.isFinite(num) || num < 1 || num > 30) return String(num || "");
  const units = ["", "א", "ב", "ג", "ד", "ה", "ו", "ז", "ח", "ט"];
  let letters = "";
  if (num <= 9) letters = units[num];
  else if (num === 10) letters = "י";
  else if (num === 11) letters = "יא";
  else if (num === 12) letters = "יב";
  else if (num === 13) letters = "יג";
  else if (num === 14) letters = "יד";
  else if (num === 15) letters = "טו";
  else if (num === 16) letters = "טז";
  else if (num === 17) letters = "יז";
  else if (num === 18) letters = "יח";
  else if (num === 19) letters = "יט";
  else if (num === 20) letters = "כ";
  else if (num <= 29) letters = `כ${units[num - 20]}`;
  else letters = "ל";
  return `${letters}׳`;
}

function hDateString(date) {
  try {
    const parts = new Intl.DateTimeFormat("he-IL-u-ca-hebrew", { weekday: "long", day: "numeric", month: "long", year: "numeric" }).formatToParts(date);
    const weekday = parts.find((p) => p.type === "weekday")?.value || "";
    const month = parts.find((p) => p.type === "month")?.value || "";
    const year = parts.find((p) => p.type === "year")?.value || "";
    const dayNum = Number(parts.find((p) => p.type === "day")?.value || 0);
    return `${weekday} ${toHebrewDayLetters(dayNum)} ${month} ${year}`.trim();
  } catch (_) {
    return date.toLocaleDateString("he-IL");
  }
}

function hebrewDayMonth(date) {
  try {
    const parts = new Intl.DateTimeFormat("he-IL-u-ca-hebrew", { day: "numeric", month: "long" }).formatToParts(date);
    const month = parts.find((p) => p.type === "month")?.value || "";
    const dayNum = Number(parts.find((p) => p.type === "day")?.value || 0);
    return `${toHebrewDayLetters(dayNum)} ${month}`.trim();
  } catch (_) {
    return "";
  }
}

function toISODate(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

const PARASHA_FALLBACK = [
  "בראשית","נח","לך לך","וירא","חיי שרה","תולדות","ויצא","וישלח","וישב","מקץ","ויגש","ויחי",
  "שמות","וארא","בא","בשלח","יתרו","משפטים","תרומה","תצוה","כי תשא","ויקהל","פקודי","ויקרא",
  "צו","שמיני","תזריע","מצורע","אחרי מות","קדושים","אמור","בהר","בחוקותי","במדבר","נשא","בהעלותך",
  "שלח","קרח","חקת","בלק","פנחס","מטות","מסעי","דברים","ואתחנן","עקב","ראה","שופטים","כי תצא",
  "כי תבוא","נצבים","וילך","האזינו","וזאת הברכה",
];

function fallbackParashaForShabbat(date) {
  const base = new Date("2025-10-18");
  const diff = Math.floor((date.getTime() - base.getTime()) / (1000 * 60 * 60 * 24 * 7));
  const idx = ((diff % PARASHA_FALLBACK.length) + PARASHA_FALLBACK.length) % PARASHA_FALLBACK.length;
  return PARASHA_FALLBACK[idx];
}

function getUpcomingShabbatDates(count = 6) {
  const result = [];
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  while (d.getDay() !== 6) d.setDate(d.getDate() + 1);
  for (let i = 0; i < count; i += 1) {
    result.push(new Date(d));
    d.setDate(d.getDate() + 7);
  }
  return result;
}

function useMaps(volunteers, domains) {
  const volMap = {};
  volunteers.forEach((v) => { volMap[v.id] = v; });
  const domMap = {};
  domains.forEach((d) => { domMap[d.id] = d; });
  return { volMap, domMap };
}

/* ═══════════════════════════════════════════
   STATUS CONFIG
   ═══════════════════════════════════════════ */

const STATUS_CONFIG = {
  matched: {
    text: "שויך מתנדב",
    color: "#059669",
    bg: "#ECFDF5",
    border: "#059669",
    ledClass: "led-green",
  },
  no_response: {
    text: "ללא מענה",
    color: "#DC2626",
    bg: "#FEF2F2",
    border: "#DC2626",
    ledClass: "led-red-blink",
  },
  none: {
    text: "פעילות סניף",
    color: "#475569",
    bg: "#F8FAFC",
    border: "#94A3B8",
    ledClass: "led-none",
  },
  pending: {
    text: "ממתין לשיוך",
    color: "#D97706",
    bg: "#FFFBEB",
    border: "#D97706",
    ledClass: "led-orange-pulse",
  },
};

function getStatus(val) {
  const s = (val || "pending").trim();
  return STATUS_CONFIG[s] || STATUS_CONFIG.pending;
}

function statusLabel(val) {
  return getStatus(val).text;
}

function getActivityStatus(activity) {
  const hasVolunteer = !!((activity?.volunteerId || "").trim());
  if (hasVolunteer) return "matched";
  const raw = (activity?.status || "").trim();
  if (!raw || raw === "matched") return "pending";
  return raw;
}

/* ═══════════════════════════════════════════
   INJECT GLOBAL STYLES
   ═══════════════════════════════════════════ */

if (typeof document !== "undefined") {
  const styleId = "activities-page-v2-styles";
  if (!document.getElementById(styleId)) {
    const sheet = document.createElement("style");
    sheet.id = styleId;
    sheet.textContent = `
      /* ── LED Animations ── */
      @keyframes ledBlink {
        0%, 100% { opacity: 1; box-shadow: 0 0 4px 2px rgba(220,38,38,.55), 0 0 12px 4px rgba(220,38,38,.25); }
        50%       { opacity: .22; box-shadow: 0 0 2px 1px rgba(220,38,38,.1); }
      }
      @keyframes ledPulse {
        0%, 100% { opacity: .85; box-shadow: 0 0 4px 2px rgba(217,119,6,.45), 0 0 10px 3px rgba(217,119,6,.18); }
        50%       { opacity: .4;  box-shadow: 0 0 2px 1px rgba(217,119,6,.08); }
      }
      .led-green {
        background: #059669;
        box-shadow: 0 0 5px 2px rgba(5,150,105,.5), 0 0 14px 4px rgba(5,150,105,.2);
      }
      .led-red-blink {
        background: #DC2626;
        animation: ledBlink 1s ease-in-out infinite;
      }
      .led-orange-pulse {
        background: #D97706;
        animation: ledPulse 2.2s ease-in-out infinite;
      }
      .led-none {
        background: #64748B;
        box-shadow: 0 0 4px 1px rgba(100,116,139,.35);
      }

      /* ── General ── */
      @keyframes slideUp {
        from { opacity: 0; transform: translate(-50%, 16px); }
        to   { opacity: 1; transform: translate(-50%, 0); }
      }
      @keyframes fadeInRow {
        from { opacity: 0; transform: translateX(8px); }
        to   { opacity: 1; transform: translateX(0); }
      }

      /* ── Table row: hover shows quick-actions, no layout shift ── */
      .act-row { transition: background .12s ease; }
      .act-row:hover { background: #F5F5FA !important; }
      .act-row:nth-child(even) { background: #FCFCFE; }
      .act-row:hover .act-row-actions { opacity: 1; }
      .act-row:hover .act-sticky-cell { background: #F5F5FA !important; }

      /* Quick actions: always visible for consistent layout */
      .act-quick-actions {
        opacity: 1;
        pointer-events: auto;
        transition: none;
      }

      /* ── Misc hovers ── */
      .act-quick-btn { transition: all .12s ease; }
      .act-quick-btn:hover { background: #EEEEF2 !important; border-color: #C8C8D4 !important; transform: translateY(-1px); }
      .act-assign-item:hover { background: #F5F5FA !important; }
      .act-chip { transition: all .12s ease; }
      .act-chip:hover { background: #F0F0F5 !important; transform: translateY(-1px); }
      .act-mobile-card { transition: box-shadow .15s ease, transform .15s ease; }
      .act-mobile-card:hover { box-shadow: 0 6px 20px rgba(0,0,0,.07); transform: translateY(-1px); }
    `;
    document.head.appendChild(sheet);
  }
}

/* ═══════════════════════════════════════════
   LED COMPONENT
   ═══════════════════════════════════════════ */

function StatusLed({ status, animated = false }) {
  const cfg = getStatus(status);
  // Only use the animated CSS class (blink/pulse) when animated=true
  // Otherwise show a static colored dot with a subtle glow
  const className = animated ? cfg.ledClass : "";
  const staticStyle = !animated ? {
    background: cfg.color,
    boxShadow: `0 0 4px 1px ${cfg.color}40`,
  } : {};
  return (
    <span
      className={className}
      style={{
        display: "inline-block",
        width: 10,
        height: 10,
        borderRadius: "50%",
        flexShrink: 0,
        ...staticStyle,
      }}
    />
  );
}

/* ═══════════════════════════════════════════
   SHARED MINI-STYLES
   ═══════════════════════════════════════════ */

const quickBtnStyle = {
  padding: "6px 8px",
  borderRadius: 8,
  fontSize: 12,
  fontWeight: 600,
  border: "1px solid #E8E8ED",
  background: "#FAFAFC",
  color: "#525264",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 4,
  fontFamily: "inherit",
  lineHeight: 1.2,
  minHeight: 34,
  textAlign: "center",
};

const iconBtnStyle = {
  ...btnGhost,
  width: 34,
  height: 34,
  borderRadius: 10,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
};

const ellipsisTextStyle = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const stickyActionCellStyle = {
  position: "sticky",
  left: 0,
  zIndex: 2,
  background: "#fff",
  boxShadow: "-10px 0 18px rgba(15,23,42,.05)",
};

/* ═══════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════ */

export default function ActivitiesPage({
  volunteers, domains, beneficiaries = [], activities, users = [], onRefresh,
  showToast, userId, contributionTypes = [], isMobile = false, addOpenToken = 0, addInitialDate = "",
  addInitial = null, canAddActivities = true, onQuickAddComplete,
}) {
  const PAGE_SIZE = 10;
  const [fDom, setFDom] = useState("all");
  const [fStatus, setFStatus] = useState("all");
  const [fVolunteer, setFVolunteer] = useState("all");
  const [fVolunteerQuery, setFVolunteerQuery] = useState("");
  const [showVolunteerDropdown, setShowVolunteerDropdown] = useState(false);
  const [mobileSummaryOpen, setMobileSummaryOpen] = useState(false);
  const [mobileDateOpen, setMobileDateOpen] = useState(false);
  const [mobileCategoryOpen, setMobileCategoryOpen] = useState(false);
  const [fCity, setFCity] = useState("all");
  const [fSource, setFSource] = useState("all");
  const [fFromDate, setFFromDate] = useState("");
  const [fToDate, setFToDate] = useState("");
  const [datePreset, setDatePreset] = useState("all");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [modal, setModal] = useState(false);
  const [addDraft, setAddDraft] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [lastDeleted, setLastDeleted] = useState(null);
  const [keepAdding, setKeepAdding] = useState(false);
  const [openedFromQuickAction, setOpenedFromQuickAction] = useState(false);
  const [assignModalItem, setAssignModalItem] = useState(null);
  const [statusModalItem, setStatusModalItem] = useState(null);
  const [assignSearch, setAssignSearch] = useState("");
  const listEndRef = useRef(null);
  const lastAddOpenTokenRef = useRef(addOpenToken);
  const { volMap, domMap } = useMaps(volunteers, domains);
  const userMap = useMemo(() => {
    const m = {};
    users.forEach((u) => {
      if (!u?.id) return;
      m[u.id] = u;
    });
    return m;
  }, [users]);
  const benMap = useMemo(() => {
    const m = {};
    beneficiaries.forEach((b) => { m[b.id] = b; });
    return m;
  }, [beneficiaries]);

  useEffect(() => {
    if (addOpenToken > (lastAddOpenTokenRef.current || 0)) {
      setOpenedFromQuickAction(true);
      setAddDraft(addInitial || null);
      setModal(true);
    }
    lastAddOpenTokenRef.current = addOpenToken;
  }, [addOpenToken, addInitial]);

  const statusFilteredActivities = useMemo(() =>
    [...activities]
      .sort((a, b) => (b.date || "").localeCompare(a.date || ""))
      .filter((a) => {
        const effectiveStatus = getActivityStatus(a);
        if (fStatus !== "all" && effectiveStatus !== fStatus) return false;
        if (fVolunteer !== "all" && (a.volunteerId || "") !== fVolunteer) return false;
        if (fVolunteer === "all" && (fVolunteerQuery || "").trim()) {
          const query = fVolunteerQuery.trim().toLowerCase();
          const volName = (volMap[a.volunteerId]?.name || "").toLowerCase();
          if (!volName.includes(query)) return false;
        }
        if (fSource !== "all") {
          const src = (a.source || "manual").trim();
          if (src !== fSource) return false;
        }
        if (fCity !== "all") {
          const city = (a.city || domMap[a.domainId]?.city || "").trim();
          if (city !== fCity) return false;
        }
        const date = (a.date || "").trim();
        if (fFromDate && (!date || date < fFromDate)) return false;
        if (fToDate && (!date || date > fToDate)) return false;
        return true;
      }),
    [activities, fStatus, fVolunteer, fVolunteerQuery, fSource, fCity, fFromDate, fToDate, volMap, domMap]
  );
  const domainOptionCounts = useMemo(() => {
    const counts = {};
    statusFilteredActivities.forEach((a) => {
      const key = a.domainId || "";
      if (!key) return;
      counts[key] = (counts[key] || 0) + 1;
    });
    return counts;
  }, [statusFilteredActivities]);
  const filteredActivities = useMemo(() =>
    statusFilteredActivities.filter((a) => (fDom === "all" ? true : a.domainId === fDom)),
    [statusFilteredActivities, fDom]
  );
  const displayedActivities = useMemo(
    () => filteredActivities.slice(0, visibleCount),
    [filteredActivities, visibleCount]
  );
  const hasMoreActivities = visibleCount < filteredActivities.length;
  const availableDomains = useMemo(
    () => domains.filter((d) => d.active !== false),
    [domains]
  );
  const cityOptions = useMemo(() => {
    const set = new Set();
    activities.forEach((a) => {
      const city = (a.city || domMap[a.domainId]?.city || "").trim();
      if (city) set.add(city);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, "he"));
  }, [activities, domMap]);

  const totalCount = filteredActivities.length;
  const totalContributionUnits = sumActivitiesContributionTotal(filteredActivities);
  const totalContributionValue = sumActivitiesContributionValueTotal(filteredActivities);
  const pendingCount = filteredActivities.filter((a) => getActivityStatus(a) === "pending").length;
  const noResponseCount = filteredActivities.filter((a) => getActivityStatus(a) === "no_response").length;
  const noneCount = filteredActivities.filter((a) => getActivityStatus(a) === "none").length;
  const matchedCount = filteredActivities.filter((a) => getActivityStatus(a) === "matched").length;

  /* ═══════════════ HANDLERS ═══════════════ */

  const handleAdd = async (form, options = {}) => {
    const keepOpen = options.keepOpen === true;
    try {
      if ((form.status || "pending") === "none" && !(form.internalCoordinatorId || "").trim()) {
        await showStyledAlert("בפעילות סניף חייבים לבחור רכז.");
        return false;
      }
      await addActivity(form, userId);
      showToast("פעילות נשמרה");
      if (!keepOpen) {
        setModal(false);
        setAddDraft(null);
      }
      if (!keepOpen && openedFromQuickAction && typeof onQuickAddComplete === "function") {
        setOpenedFromQuickAction(false);
        onQuickAddComplete();
      }
      refreshQuietly();
      return keepOpen;
    } catch (e) {
      await showStyledAlert("שגיאה בשמירת דיווח: " + e.message);
      return false;
    }
  };

  const handleDelete = async (id) => {
    if (!await showStyledConfirm("למחוק פעילות זו?")) return;
    try {
      const target = activities.find((x) => x.id === id);
      await deleteActivity(id);
      if (target) setLastDeleted(target);
      showToast("נמחק");
      refreshQuietly();
    } catch (e) {
      await showStyledAlert("שגיאה: " + e.message);
    }
  };

  const undoDelete = async () => {
    if (!lastDeleted) return;
    try {
      const restored = { ...lastDeleted };
      delete restored.id;
      delete restored.createdAt;
      await addActivity(restored, userId);
      setLastDeleted(null);
      showToast("המחיקה בוטלה");
      refreshQuietly();
    } catch (e) {
      await showStyledAlert("שגיאה בביטול מחיקה: " + e.message);
    }
  };

  const handleEdit = async (form) => {
    const hasVolunteer = !!(form.volunteerId || "").trim();
    try {
      await updateActivity(editItem.id, {
        volunteerId: form.volunteerId,
        beneficiaryId: form.beneficiaryId || "",
        domainId: form.domainId,
        city: form.city || "",
        internalCoordinatorId: form.status === "none" ? (form.internalCoordinatorId || "") : "",
        contributions: normalizeActivityContributions(form.contributions),
        subdomain: form.subdomain || "",
        date: form.date,
        time: form.time,
        duration: Number(form.duration),
        status: hasVolunteer ? "matched" : ((form.status || "pending") === "matched" ? "pending" : (form.status || "pending")),
        notes: form.notes || "",
      });
      showToast("פעילות עודכנה");
      setEditItem(null);
      refreshQuietly();
    } catch (e) {
      await showStyledAlert("שגיאה: " + e.message);
    }
  };

  const buildDuplicateDraft = useCallback((activity) => ({
    volunteerId: activity?.volunteerId || "",
    beneficiaryId: activity?.beneficiaryId || "",
    domainId: activity?.domainId || "",
    city: activity?.city || "",
    internalCoordinatorId: activity?.internalCoordinatorId || "",
    subdomain: activity?.subdomain || "",
    date: activity?.date || fmtDate(new Date()),
    time: activity?.time || "09:00",
    duration: Number(activity?.duration ?? 1),
    contributions: normalizeActivityContributions(activity?.contributions),
    status: (activity?.volunteerId || "") ? "matched" : ((activity?.status === "matched" ? "pending" : activity?.status) || "pending"),
    notes: activity?.notes || "",
  }), []);

  const openDuplicateModal = useCallback((activity) => {
    setEditItem(null);
    setOpenedFromQuickAction(false);
    setAddDraft(buildDuplicateDraft(activity));
    setModal(true);
  }, [buildDuplicateDraft]);

  const markNoResponse = async (activityId) => {
    try {
      await updateActivity(activityId, { status: "no_response", volunteerId: "" });
      showToast("סומן: לא היה מענה");
      refreshQuietly();
    } catch (e) {
      await showStyledAlert("שגיאה בעדכון סטטוס: " + e.message);
    }
  };

  const markPending = async (activityId) => {
    try {
      await updateActivity(activityId, { status: "pending", volunteerId: "" });
      showToast("סומן: ממתין לשיוך");
      refreshQuietly();
    } catch (e) {
      await showStyledAlert("שגיאה בעדכון סטטוס: " + e.message);
    }
  };

  const markNone = async (activityId) => {
    try {
      await updateActivity(activityId, { status: "none", volunteerId: "", internalCoordinatorId: "" });
      showToast("סומן: פעילות סניף");
      refreshQuietly();
    } catch (e) {
      await showStyledAlert("שגיאה בעדכון סטטוס: " + e.message);
    }
  };

  const setMatchedVolunteer = async (activityId, volunteerId) => {
    try {
      await updateActivity(activityId, { status: "matched", volunteerId: volunteerId || "" });
      showToast("שויך מתנדב");
      setAssignModalItem(null);
      setAssignSearch("");
      refreshQuietly();
    } catch (e) {
      await showStyledAlert("שגיאה בשיוך מתנדב: " + e.message);
    }
  };

  const openStatusEditor = (activity) => {
    setStatusModalItem(activity);
  };

  const changeStatusFromModal = async (fn, activityId) => {
    setStatusModalItem(null);
    await fn(activityId);
  };

  const openBranchActivityEditor = (activity) => {
    if (!activity) return;
    setStatusModalItem(null);
    setEditItem({
      ...activity,
      volunteerId: "",
      status: "none",
      internalCoordinatorId: (activity.internalCoordinatorId || "").trim(),
    });
  };

  const exportActivitiesExcel = () => {
    const rows = filteredActivities.map((a) => ({
      "מתנדב": volMap[a.volunteerId]?.name || "",
      "נעזר": benMap[a.beneficiaryId]?.name || "",
      "תחום": domMap[a.domainId]?.name || "",
      "עיר": a.city || domMap[a.domainId]?.city || "",
      "תאריך": a.date || "",
      "שעה": a.time || "",
      "משך (שעות)": Number(a.duration || 0),
      "תרומות": getActivityContributionSummary(a),
      "סה\"כ פריטים": getActivityContributionTotal(a),
      "ערך תרומות בש\"ח": getActivityContributionValueTotal(a),
      "סטטוס מענה": statusLabel(getActivityStatus(a)),
      "מקור": a.source === "auto" ? "אוטומטי" : "ידני",
      "הערות": a.notes || "",
    }));
    exportToXlsx(rows, "Activities", `activities-${fmtDate(new Date())}.xlsx`);
  };

  const activeVolunteers = useMemo(
    () => volunteers.filter((v) => v.status === "active"),
    [volunteers]
  );
  const filteredAssignVolunteers = useMemo(() => {
    const q = (assignSearch || "").trim().toLowerCase();
    if (!q) return activeVolunteers;
    return activeVolunteers.filter((v) => {
      const hay = [v.name || "", `${v.firstName || ""} ${v.lastName || ""}`.trim(), v.phone || "", v.city || ""].join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [activeVolunteers, assignSearch]);
  const filteredVolunteerOptions = useMemo(() => {
    const q = (fVolunteerQuery || "").trim().toLowerCase();
    const active = volunteers.filter((v) => v.status === "active");
    if (!q) return active.slice(0, 20);
    return active
      .filter((v) => (v.name || "").toLowerCase().includes(q))
      .slice(0, 20);
  }, [volunteers, fVolunteerQuery]);

  const refreshQuietly = useCallback(() => {
    if (typeof onRefresh !== "function") return;
    Promise.resolve(onRefresh()).catch((e) => {
      console.warn("Activities refresh warning:", e);
    });
  }, [onRefresh]);

  const coordinatorLabelForActivity = useCallback((activity) => {
    const volunteer = volMap[(activity?.volunteerId || "").trim()];
    if (!volunteer) {
      const internalCoordinatorId = (activity?.internalCoordinatorId || "").trim();
      if (!internalCoordinatorId) return "—";
      const internalCoordinator = userMap[internalCoordinatorId];
      return internalCoordinator?.name || internalCoordinator?.email || internalCoordinatorId;
    }

    let coordinatorId = getPrimaryCoordinatorId(volunteer);
    if (!coordinatorId && getPrimaryTeamLeadId(volunteer)) {
      const teamLead = volMap[getPrimaryTeamLeadId(volunteer)];
      coordinatorId = getPrimaryCoordinatorId(teamLead);
    }
    if (!coordinatorId) {
      coordinatorId = (volunteer.groupCoordinatorId || "").trim(); // legacy fallback
    }
    if (!coordinatorId) return "—";

    const user = userMap[coordinatorId];
    return user?.name || user?.email || coordinatorId;
  }, [userMap, volMap]);

  const applyDatePreset = useCallback((preset) => {
    const now = new Date();
    const currentDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    if (preset === "custom") {
      setDatePreset("custom");
      return;
    }
    if (preset === "all") {
      setFFromDate("");
      setFToDate("");
      setDatePreset("all");
      return;
    }
    if (preset === "today") {
      const d = fmtDate(currentDay);
      setFFromDate(d);
      setFToDate(d);
      setDatePreset("today");
      return;
    }
    if (preset === "yesterday") {
      const y = new Date(currentDay);
      y.setDate(y.getDate() - 1);
      const d = fmtDate(y);
      setFFromDate(d);
      setFToDate(d);
      setDatePreset("yesterday");
      return;
    }
    if (preset === "week") {
      const start = new Date(currentDay);
      start.setDate(start.getDate() - start.getDay());
      setFFromDate(fmtDate(start));
      setFToDate(fmtDate(currentDay));
      setDatePreset("week");
      return;
    }
    if (preset === "month") {
      const start = new Date(currentDay.getFullYear(), currentDay.getMonth(), 1);
      setFFromDate(fmtDate(start));
      setFToDate(fmtDate(currentDay));
      setDatePreset("month");
    }
  }, []);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [fDom, fStatus, fVolunteer, fVolunteerQuery, fSource, fCity, fFromDate, fToDate, activities.length]);

  useEffect(() => {
    if (fDom === "all") return;
    if (!availableDomains.some((d) => d.id === fDom)) {
      setFDom("all");
    }
  }, [fDom, availableDomains]);
  useEffect(() => {
    if (fVolunteer === "all") return;
    if (!volunteers.some((v) => v.id === fVolunteer && v.status === "active")) {
      setFVolunteer("all");
    }
  }, [fVolunteer, volunteers]);

  useEffect(() => {
    if (!hasMoreActivities || !listEndRef.current) return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) {
        setVisibleCount((prev) => Math.min(prev + PAGE_SIZE, filteredActivities.length));
      }
    }, { rootMargin: "120px 0px" });
    observer.observe(listEndRef.current);
    return () => observer.disconnect();
  }, [hasMoreActivities, filteredActivities.length]);

  /* ═══════════════ RENDER ═══════════════ */

  const tdBase = {
    padding: "12px 12px",
    borderBottom: "1px solid #F3F3F6",
    fontSize: 13,
    color: "#2D2D3F",
    verticalAlign: "middle",
    lineHeight: 1.5,
  };
  const datePresetButtons = [
    { id: "all", label: "הכל" },
    { id: "today", label: "היום" },
    { id: "yesterday", label: "אתמול" },
    { id: "week", label: "השבוע" },
    { id: "month", label: "החודש" },
    { id: "custom", label: "טווח" },
  ];
  const mobileSectionButtonStyle = {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    border: "1px solid #E5E7EB",
    background: "linear-gradient(180deg, #FFFFFF, #FAFAFB)",
    boxShadow: "0 8px 20px rgba(15,23,42,.035)",
    color: "#0F172A",
    borderRadius: 14,
    padding: "10px 14px",
    minHeight: 56,
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
    fontFamily: "inherit",
  };
  const mobileSectionPanelStyle = {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: 10,
    padding: 12,
    borderRadius: 18,
    background: "linear-gradient(180deg, #FFFFFF, #FAFAFC)",
    border: "1px solid #E7EAF0",
    boxShadow: "0 10px 24px rgba(15,23,42,.035)",
  };

  return (
    <div style={{ fontFamily: "'Heebo', 'Assistant', sans-serif", direction: "rtl" }}>

      {/* ════════ Top header card ════════ */}
      <div style={{
        background: "#fff",
        borderRadius: 16,
        border: "1px solid #E8E8ED",
        padding: "16px 20px",
        marginBottom: 14,
        boxShadow: "0 1px 4px rgba(0,0,0,.03)",
      }}>

        {/* Row 1: Title + Actions */}
        <div style={{
          display: "flex", justifyContent: "space-between",
          alignItems: isMobile ? "stretch" : "center", gap: 12,
          marginBottom: 16, flexWrap: "wrap",
          flexDirection: isMobile ? "column" : "row",
        }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12, flex: 1, minWidth: 0 }}>
            {isMobile ? (
              <>
                <div>
                  <div style={{ fontSize: 24, fontWeight: 800, fontFamily: "Assistant", color: "#0F172A", marginBottom: 4 }}>פעילויות</div>
                  <div style={{ fontSize: 13, color: "#94A3B8" }}>חיפוש מהיר, סינון מתקדם וסיכומי פעילות במבנה נקי.</div>
                </div>

                <div
                  style={{ position: "relative", width: "100%" }}
                  onFocus={() => setShowVolunteerDropdown(true)}
                  onBlur={() => setTimeout(() => setShowVolunteerDropdown(false), 120)}
                >
                  <input
                    value={fVolunteerQuery}
                    onChange={(e) => {
                      const nextQuery = e.target.value;
                      setFVolunteerQuery(nextQuery);
                      if (!nextQuery.trim()) {
                        setFVolunteer("all");
                        return;
                      }
                      const normalized = nextQuery.trim().toLowerCase();
                      const exact = volunteers.find(
                        (v) => v.status === "active" && ((v.name || "").trim().toLowerCase() === normalized)
                      );
                      setFVolunteer(exact ? exact.id : "all");
                    }}
                    placeholder="חיפוש מתנדב..."
                    style={{
                      ...inputStyle,
                      borderRadius: 16,
                      padding: "13px 44px 13px 14px",
                      fontSize: 14,
                      fontWeight: 500,
                      width: "100%",
                      fontFamily: "inherit",
                      border: "1px solid #E5E7EB",
                      background: "linear-gradient(180deg, #FFFFFF, #FCFCFD)",
                      boxShadow: "0 6px 18px rgba(15,23,42,.04)",
                    }}
                  />
                  <span style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", color: "#9CA3AF", pointerEvents: "none" }}>
                    <Ic.Search />
                  </span>
                  {showVolunteerDropdown && filteredVolunteerOptions.length > 0 && (
                    <div style={{
                      position: "absolute",
                      top: "calc(100% + 6px)",
                      right: 0,
                      left: 0,
                      maxHeight: 220,
                      overflowY: "auto",
                      background: "#fff",
                      border: "1px solid #E2E2E8",
                      borderRadius: 12,
                      boxShadow: "0 8px 24px rgba(0,0,0,.08)",
                      zIndex: 20,
                    }}>
                      {filteredVolunteerOptions.map((v) => (
                        <button
                          key={v.id}
                          type="button"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            setFVolunteer(v.id);
                            setFVolunteerQuery(v.name || "");
                            setShowVolunteerDropdown(false);
                          }}
                          style={{
                            width: "100%",
                            border: "none",
                            borderBottom: "1px solid #F3F3F6",
                            background: "#fff",
                            textAlign: "right",
                            padding: "10px 12px",
                            cursor: "pointer",
                            fontSize: 13,
                            color: "#1A1A2E",
                            fontFamily: "inherit",
                          }}
                        >
                          {v.name || "ללא שם"}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div style={{ fontSize: 12, fontWeight: 800, color: "#94A3B8", letterSpacing: ".02em", paddingInline: 2 }}>פילטרים</div>

                <button
                  type="button"
                  onClick={() => setMobileDateOpen((prev) => !prev)}
                  style={mobileSectionButtonStyle}
                >
                  <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ width: 28, height: 28, borderRadius: 10, background: "#F3F4F6", display: "inline-flex", alignItems: "center", justifyContent: "center", color: "#475569" }}>
                      <Ic.Calendar />
                    </span>
                    <span>פילטר תאריכים</span>
                  </span>
                  <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 28, height: 28, borderRadius: 10, background: mobileDateOpen ? "#FFF3D6" : "#F8FAFC", color: mobileDateOpen ? "#8A5A00" : "#64748B", transform: mobileDateOpen ? "rotate(-90deg)" : "rotate(90deg)", transition: "transform .18s ease, background .18s ease, color .18s ease" }}>
                    <Ic.Back />
                  </span>
                </button>

                {mobileDateOpen && (
                  <div style={mobileSectionPanelStyle}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                      <div>
                        <div style={{ fontSize: 11, color: "#94A3B8", marginBottom: 4 }}>מתאריך</div>
                        <input
                          type="date"
                          value={fFromDate}
                          onChange={(e) => { setDatePreset("custom"); setFFromDate(e.target.value); }}
                          style={{ ...inputStyle, borderRadius: 14, padding: "10px 12px", fontSize: 14, width: "100%", border: "1px solid #E5E7EB", background: "#fff" }}
                          title="מתאריך"
                        />
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: "#94A3B8", marginBottom: 4 }}>עד תאריך</div>
                        <input
                          type="date"
                          value={fToDate}
                          onChange={(e) => { setDatePreset("custom"); setFToDate(e.target.value); }}
                          style={{ ...inputStyle, borderRadius: 14, padding: "10px 12px", fontSize: 14, width: "100%", border: "1px solid #E5E7EB", background: "#fff" }}
                          title="עד תאריך"
                        />
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
                      {datePresetButtons.map((opt) => {
                        const active = datePreset === opt.id;
                        return (
                          <button
                            key={opt.id}
                            type="button"
                            onClick={() => applyDatePreset(opt.id)}
                            style={{
                              border: active ? "1px solid #D6A54F" : "1px solid #E5E7EB",
                              background: active ? "linear-gradient(180deg, #FFF9EC, #FFF3D6)" : "#fff",
                              color: active ? "#8A5A00" : "#475569",
                              borderRadius: 999,
                              padding: "7px 12px",
                              fontSize: 12,
                              fontWeight: 700,
                              cursor: "pointer",
                              fontFamily: "inherit",
                              boxShadow: active ? "0 4px 10px rgba(214,165,79,.18)" : "none",
                            }}
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                <button
                  type="button"
                  onClick={() => setMobileCategoryOpen((prev) => !prev)}
                  style={mobileSectionButtonStyle}
                >
                  <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ width: 28, height: 28, borderRadius: 10, background: "#F3F4F6", display: "inline-flex", alignItems: "center", justifyContent: "center", color: "#475569" }}>
                      <Ic.Grid />
                    </span>
                    <span>פילטר קטגוריות</span>
                  </span>
                  <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 28, height: 28, borderRadius: 10, background: mobileCategoryOpen ? "#FFF3D6" : "#F8FAFC", color: mobileCategoryOpen ? "#8A5A00" : "#64748B", transform: mobileCategoryOpen ? "rotate(-90deg)" : "rotate(90deg)", transition: "transform .18s ease, background .18s ease, color .18s ease" }}>
                    <Ic.Back />
                  </span>
                </button>

                {mobileCategoryOpen && (
                  <div style={mobileSectionPanelStyle}>
                    <div>
                      <div style={{ fontSize: 11, color: "#94A3B8", marginBottom: 4 }}>תחום</div>
                      <select
                        value={fDom}
                        onChange={(e) => setFDom(e.target.value)}
                        style={{ ...inputStyle, borderRadius: 14, padding: "10px 12px", fontSize: 14, width: "100%", border: "1px solid #E5E7EB", background: "#fff" }}
                      >
                        <option value="all">כל התחומים</option>
                        {availableDomains.map((d) => (
                          <option key={d.id} value={d.id}>
                            {d.name} ({domainOptionCounts[d.id] || 0})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <div style={{ fontSize: 11, color: "#94A3B8", marginBottom: 4 }}>סטטוס</div>
                      <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} style={{ ...inputStyle, borderRadius: 14, padding: "10px 12px", fontSize: 14, width: "100%", border: "1px solid #E5E7EB", background: "#fff" }}>
                        <option value="all">כל הסטטוסים</option>
                        <option value="matched">שויך מתנדב</option>
                        <option value="pending">ממתין לשיוך</option>
                        <option value="no_response">ללא מענה</option>
                        <option value="none">פעילות סניף</option>
                      </select>
                    </div>
                    <div>
                      <div style={{ fontSize: 11, color: "#94A3B8", marginBottom: 4 }}>מקור</div>
                      <select value={fSource} onChange={(e) => setFSource(e.target.value)} style={{ ...inputStyle, borderRadius: 14, padding: "10px 12px", fontSize: 14, width: "100%", border: "1px solid #E5E7EB", background: "#fff" }}>
                        <option value="all">כל המקורות</option>
                        <option value="manual">ידני</option>
                        <option value="auto">אוטומטי</option>
                      </select>
                    </div>
                    {cityOptions.length > 0 && (
                      <div>
                        <div style={{ fontSize: 11, color: "#94A3B8", marginBottom: 4 }}>עיר</div>
                        <select value={fCity} onChange={(e) => setFCity(e.target.value)} style={{ ...inputStyle, borderRadius: 14, padding: "10px 12px", fontSize: 14, width: "100%", border: "1px solid #E5E7EB", background: "#fff" }}>
                          <option value="all">כל הערים</option>
                          {cityOptions.map((city) => <option key={city} value={city}>{city}</option>)}
                        </select>
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            {/* Domain dropdown */}
            <div style={{ position: "relative" }}>
              <select
                value={fDom}
                onChange={(e) => setFDom(e.target.value)}
                style={{
                  ...inputStyle,
                  borderRadius: 12,
                  padding: "9px 36px 9px 14px",
                  fontSize: 14,
                  fontWeight: 600,
                  minWidth: 180,
                  cursor: "pointer",
                  appearance: "none",
                  backgroundImage: `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%238E8EA0' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "left 12px center",
                  color: fDom === "all" ? "#1A1A2E" : (domMap[fDom]?.color || "#1A1A2E"),
                  borderColor: fDom === "all" ? "#E2E2E8" : (domMap[fDom]?.color || "#E2E2E8") + "60",
                  fontFamily: "inherit",
                }}
              >
                <option value="all">כל התחומים</option>
                {availableDomains.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name} ({domainOptionCounts[d.id] || 0})
                  </option>
                ))}
              </select>
              {/* Color indicator dot next to dropdown */}
              {fDom !== "all" && domMap[fDom] && (
                <span style={{
                  position: "absolute",
                  right: 12,
                  top: "50%",
                  transform: "translateY(-50%)",
                  width: 8, height: 8,
                  borderRadius: "50%",
                  background: domMap[fDom].color,
                  boxShadow: `0 0 0 2px ${domMap[fDom].color}30`,
                  pointerEvents: "none",
                }} />
              )}
            </div>
            <div style={{ position: "relative" }}>
              <select
                value={fStatus}
                onChange={(e) => setFStatus(e.target.value)}
                style={{
                  ...inputStyle,
                  borderRadius: 12,
                  padding: "9px 30px 9px 14px",
                  fontSize: 14,
                  fontWeight: 600,
                  minWidth: 170,
                  cursor: "pointer",
                  appearance: "none",
                  backgroundImage: `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%238E8EA0' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "left 12px center",
                  fontFamily: "inherit",
                }}
              >
                <option value="all">כל הסטטוסים</option>
                <option value="matched">שויך מתנדב</option>
                <option value="pending">ממתין לשיוך</option>
                <option value="no_response">ללא מענה</option>
                <option value="none">פעילות סניף</option>
              </select>
            </div>
            <div style={{ position: "relative" }}>
              <select
                value={fSource}
                onChange={(e) => setFSource(e.target.value)}
                style={{
                  ...inputStyle,
                  borderRadius: 12,
                  padding: "9px 30px 9px 14px",
                  fontSize: 14,
                  fontWeight: 600,
                  minWidth: 170,
                  cursor: "pointer",
                  appearance: "none",
                  backgroundImage: `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%238E8EA0' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "left 12px center",
                  fontFamily: "inherit",
                }}
              >
                <option value="all">כל המקורות</option>
                <option value="manual">ידני</option>
                <option value="auto">אוטומטי</option>
              </select>
            </div>
            <div
              style={{ position: "relative", minWidth: 220 }}
              onFocus={() => setShowVolunteerDropdown(true)}
              onBlur={() => setTimeout(() => setShowVolunteerDropdown(false), 120)}
            >
              <input
                value={fVolunteerQuery}
                onChange={(e) => {
                  const nextQuery = e.target.value;
                  setFVolunteerQuery(nextQuery);
                  if (!nextQuery.trim()) {
                    setFVolunteer("all");
                    return;
                  }
                  const normalized = nextQuery.trim().toLowerCase();
                  const exact = volunteers.find(
                    (v) => v.status === "active" && ((v.name || "").trim().toLowerCase() === normalized)
                  );
                  setFVolunteer(exact ? exact.id : "all");
                }}
                placeholder="חיפוש מתנדב..."
                style={{
                  ...inputStyle,
                  borderRadius: 12,
                  padding: "9px 12px",
                  fontSize: 14,
                  fontWeight: 600,
                  width: "100%",
                  fontFamily: "inherit",
                }}
              />
              {showVolunteerDropdown && filteredVolunteerOptions.length > 0 && (
                <div style={{
                  position: "absolute",
                  top: "calc(100% + 6px)",
                  right: 0,
                  left: 0,
                  maxHeight: 220,
                  overflowY: "auto",
                  background: "#fff",
                  border: "1px solid #E2E2E8",
                  borderRadius: 12,
                  boxShadow: "0 8px 24px rgba(0,0,0,.08)",
                  zIndex: 20,
                }}>
                  {filteredVolunteerOptions.map((v) => (
                    <button
                      key={v.id}
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        setFVolunteer(v.id);
                        setFVolunteerQuery(v.name || "");
                        setShowVolunteerDropdown(false);
                      }}
                      style={{
                        width: "100%",
                        border: "none",
                        borderBottom: "1px solid #F3F3F6",
                        background: "#fff",
                        textAlign: "right",
                        padding: "10px 12px",
                        cursor: "pointer",
                        fontSize: 13,
                        color: "#1A1A2E",
                        fontFamily: "inherit",
                      }}
                    >
                      {v.name || "ללא שם"}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
              {cityOptions.length > 0 && (
                <select
                  value={fCity}
                  onChange={(e) => setFCity(e.target.value)}
                  style={{ ...inputStyle, borderRadius: 12, padding: "9px 12px", fontSize: 14, minWidth: 150 }}
                  title="עיר"
                >
                  <option value="all">כל הערים (לפי תת-תחום)</option>
                  {cityOptions.map((city) => <option key={city} value={city}>{city}</option>)}
                </select>
              )}
              {datePresetButtons.map((opt) => {
                const active = datePreset === opt.id;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => applyDatePreset(opt.id)}
                    style={{
                      border: active ? "1px solid #C41E3A" : "1px solid #E2E2E8",
                      background: active ? "#FFF1F3" : "#fff",
                      color: active ? "#C41E3A" : "#525264",
                      borderRadius: 10,
                      padding: "7px 10px",
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: "pointer",
                      fontFamily: "inherit",
                    }}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
            {datePreset === "custom" && (
              <>
                <input
                  type="date"
                  value={fFromDate}
                  onChange={(e) => { setDatePreset("custom"); setFFromDate(e.target.value); }}
                  style={{ ...inputStyle, borderRadius: 12, padding: "9px 12px", fontSize: 14, minWidth: 150 }}
                  title="מתאריך"
                />
                <input
                  type="date"
                  value={fToDate}
                  onChange={(e) => { setDatePreset("custom"); setFToDate(e.target.value); }}
                  style={{ ...inputStyle, borderRadius: 12, padding: "9px 12px", fontSize: 14, minWidth: 150 }}
                  title="עד תאריך"
                />
              </>
            )}
              </div>
            )}
          </div>

          {!isMobile && (
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <button onClick={exportActivitiesExcel} style={{
                ...btnSecondary, borderRadius: 12, padding: "9px 16px", fontSize: 13, fontWeight: 500,
                display: "inline-flex", alignItems: "center", gap: 6,
              }}>ייצוא</button>
              {canAddActivities && <button onClick={() => { setOpenedFromQuickAction(false); setAddDraft(null); setModal(true); }} style={{
                ...btnPrimary, borderRadius: 12, padding: "9px 20px", fontSize: 14, fontWeight: 600,
                display: "inline-flex", alignItems: "center", gap: 6, boxShadow: "0 2px 8px rgba(196,30,58,.18)",
              }}><Ic.Plus /> הזנת פעילות</button>}
            </div>
          )}
        </div>

        {/* Row 2: Stats counters + unresolved domains */}
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
        }}>
          {isMobile ? (
            <div style={{ width: "100%", display: "grid", gap: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: "#94A3B8", letterSpacing: ".02em", paddingInline: 2 }}>סיכומי נתונים</div>
              <button
                type="button"
                onClick={() => setMobileSummaryOpen((prev) => !prev)}
                style={mobileSectionButtonStyle}
              >
                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ width: 28, height: 28, borderRadius: 10, background: "#F3F4F6", display: "inline-flex", alignItems: "center", justifyContent: "center", color: "#475569" }}>
                    <Ic.Chart />
                  </span>
                  <span>סיכומי נתונים</span>
                </span>
                <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 28, height: 28, borderRadius: 10, background: mobileSummaryOpen ? "#FFF3D6" : "#F8FAFC", color: mobileSummaryOpen ? "#8A5A00" : "#64748B", transform: mobileSummaryOpen ? "rotate(-90deg)" : "rotate(90deg)", transition: "transform .18s ease, background .18s ease, color .18s ease" }}>
                  <Ic.Back />
                </span>
              </button>
              {mobileSummaryOpen && (
                <div style={mobileSectionPanelStyle}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                    {[
                      { label: "סה\"כ פעילויות", value: totalCount, bg: "#F5F5F7", color: "#2D2D3F" },
                      { label: "ממתינים לשיוך", value: pendingCount, bg: "#FFFBEB", color: "#D97706" },
                      { label: "ללא מענה", value: noResponseCount, bg: "#FEF2F2", color: "#DC2626" },
                      { label: "פעילות סניף", value: noneCount, bg: "#F8FAFC", color: "#475569" },
                      { label: "שויכו", value: matchedCount, bg: "#ECFDF5", color: "#059669" },
                      { label: "תרומות", value: totalContributionUnits, bg: "#FFF7ED", color: "#9A3412" },
                    ].map((card) => (
                      <div key={card.label} style={{ borderRadius: 14, padding: "12px 10px", background: card.bg, border: "1px solid #ECEFF3", textAlign: "center" }}>
                        <div style={{ fontSize: 11, color: "#94A3B8", marginBottom: 4 }}>{card.label}</div>
                        <div style={{ fontSize: 20, fontWeight: 800, color: card.color, fontFamily: "Assistant" }}>{card.value}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ borderRadius: 14, padding: "12px 10px", background: "#ECFEFF", border: "1px solid #CFFAFE", textAlign: "center" }}>
                    <div style={{ fontSize: 11, color: "#94A3B8", marginBottom: 4 }}>שווי פעילות</div>
                    <div style={{ fontSize: 20, fontWeight: 800, color: "#0F766E", fontFamily: "Assistant" }}>{formatContributionCurrency(totalContributionValue) || "—"}</div>
                  </div>
                </div>
              )}
            </div>
          ) : (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "6px 14px", borderRadius: 10,
              background: "#F5F5F7", fontSize: 13, color: "#2D2D3F", fontWeight: 600,
            }}>
              סה״כ {totalCount}
            </span>
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "6px 14px", borderRadius: 10,
              background: "#FFFBEB", fontSize: 13, fontWeight: 600,
            }}>
              <StatusLed status="pending" />
              <span style={{ color: "#D97706" }}>ממתינים לשיוך {pendingCount}</span>
            </span>
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "6px 14px", borderRadius: 10,
              background: "#FEF2F2", fontSize: 13, fontWeight: 600,
            }}>
              <StatusLed status="no_response" />
              <span style={{ color: "#DC2626" }}>ללא מענה {noResponseCount}</span>
            </span>
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "6px 14px", borderRadius: 10,
              background: "#F8FAFC", fontSize: 13, fontWeight: 600,
            }}>
              <StatusLed status="none" />
              <span style={{ color: "#475569" }}>פעילות סניף {noneCount}</span>
            </span>
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "6px 14px", borderRadius: 10,
              background: "#ECFDF5", fontSize: 13, fontWeight: 600,
            }}>
              <StatusLed status="matched" />
              <span style={{ color: "#059669" }}>שויכו {matchedCount}</span>
            </span>
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "6px 14px", borderRadius: 10,
              background: "#FFF7ED", fontSize: 13, fontWeight: 600,
            }}>
              <span style={{ color: "#9A3412" }}>תרומות {totalContributionUnits}</span>
            </span>
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "6px 14px", borderRadius: 10,
              background: "#ECFEFF", fontSize: 13, fontWeight: 600,
            }}>
              <span style={{ color: "#0F766E" }}>שווי {formatContributionCurrency(totalContributionValue) || "—"}</span>
            </span>
          </div>
          )}

          
        </div>
      </div>

      {/* ════════ Table / Mobile ════════ */}
      <div style={{
        background: "#fff", borderRadius: 16, border: "1px solid #E8E8ED",
        overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,.04)",
      }}>
        {!isMobile ? (
          <div style={{ overflowX: "auto" }}>
            {/* tableLayout: fixed + colgroup = no width shift on hover */}
            <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, minWidth: 1020, tableLayout: "fixed" }}>
              <colgroup>
                <col style={{ width: 4 }} />
                <col style={{ width: "12%" }} />   {/* מתנדב */}
                <col style={{ width: "10%" }} />   {/* רכז */}
                <col style={{ width: "10%" }} />   {/* נעזר */}
                <col style={{ width: "14%" }} />   {/* תחום */}
                <col style={{ width: "9%" }} />   {/* תאריך */}
                <col style={{ width: "6%" }} />    {/* שעה */}
                <col style={{ width: "6%" }} />    {/* משך */}
                <col style={{ width: "12%" }} />   {/* תרומות */}
                <col style={{ width: "15%" }} />   {/* סטטוס */}
                <col style={{ width: "7%" }} />    {/* מקור */}
                <col style={{ width: 144 }} />    {/* פעולות */}
              </colgroup>
              <thead>
                <tr>
                  <th style={{ background: "#FAFAFC", borderBottom: "1px solid #E8E8ED", padding: 0 }} />
                  {["מתנדב", "רכז", "נעזר", "תחום", "תאריך", "שעה", "משך", "תרומות", "סטטוס", "מקור", ""].map((h, headerIdx, arr) => (
                    <th key={h} style={{
                      padding: "11px 12px", fontSize: 11, fontWeight: 600, color: "#8E8EA0",
                      textAlign: "right", background: "#FAFAFC", borderBottom: "1px solid #E8E8ED",
                      whiteSpace: "nowrap", letterSpacing: ".02em",
                      ...(headerIdx === arr.length - 1 ? { ...stickyActionCellStyle, background: "#FAFAFC", zIndex: 3 } : {}),
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {displayedActivities.map((a, idx) => {
                  const effectiveStatus = getActivityStatus(a);
                  const si = getStatus(effectiveStatus);
                  const hasVolunteerAssigned = !!((a.volunteerId || "").trim());
                  return (
                    <tr key={a.id} className="act-row" style={{ animation: `fadeInRow .25s ease ${Math.min(idx * 0.02, 0.4)}s both` }}>
                      {/* status color strip */}
                      <td style={{ padding: 0, borderBottom: "1px solid #F3F3F6", background: si.border, width: 4 }} />

                      <td style={{ ...tdBase, fontWeight: 600 }}>
                        <div style={ellipsisTextStyle} title={volMap[a.volunteerId]?.name || ""}>
                          {volMap[a.volunteerId]?.name || <span style={{ color: "#B0B0C0" }}>—</span>}
                        </div>
                      </td>
                      <td style={tdBase}>
                        <span style={{ ...ellipsisTextStyle, display: "block", fontWeight: 600, color: "#3F3F55" }} title={coordinatorLabelForActivity(a)}>
                          {coordinatorLabelForActivity(a)}
                        </span>
                      </td>
                      <td style={tdBase}>
                        <div style={ellipsisTextStyle} title={benMap[a.beneficiaryId]?.name || ""}>
                          {benMap[a.beneficiaryId]?.name || <span style={{ color: "#B0B0C0" }}>—</span>}
                        </div>
                      </td>
                      <td style={tdBase}>
                        {domMap[a.domainId] ? <DomainTag domain={domMap[a.domainId]} /> : "—"}
                        {!!(domMap[a.domainId]?.city || "").trim() && (
                          <div style={{ fontSize: 11, color: "#6B7280", marginTop: 3 }}>
                            עיר: {a.city || domMap[a.domainId].city}
                          </div>
                        )}
                        {!!(domMap[a.domainId]?.parentDomainId || "").trim() && (
                          <div style={{ fontSize: 11, color: "#6B7280", marginTop: 3 }}>
                            קטגוריית אב: {domMap[(domMap[a.domainId]?.parentDomainId || "").trim()]?.name || "—"}
                          </div>
                        )}
                        {a.subdomain && <div style={{ fontSize: 12, color: "#8E8EA0", marginTop: 3 }}>{a.subdomain}</div>}
                      </td>
                      <td style={{ ...tdBase, whiteSpace: "nowrap" }}>{a.date || ""}</td>
                      <td style={{ ...tdBase, whiteSpace: "nowrap" }}>{a.time || ""}</td>
                      <td style={{ ...tdBase, whiteSpace: "nowrap" }}><b>{a.duration || 0}</b> <span style={{ fontSize: 11, color: "#8E8EA0" }}>ש׳</span></td>
                      <td style={tdBase}>
                        {getActivityContributionTotal(a) > 0 ? (
                          <>
                            <div style={{ fontWeight: 700, color: "#9A3412" }}>{getActivityContributionTotal(a)} פריטים</div>
                            <div style={{ fontSize: 11, color: "#6B7280", marginTop: 3 }}>
                              {getActivityContributionSummary(a)}
                            </div>
                            {getActivityContributionValueTotal(a) > 0 && (
                              <div style={{ fontSize: 11, color: "#0F766E", marginTop: 3, fontWeight: 700 }}>
                                שווי: {formatContributionCurrency(getActivityContributionValueTotal(a))}
                              </div>
                            )}
                          </>
                        ) : (
                          <span style={{ color: "#B0B0C0" }}>—</span>
                        )}
                      </td>

                      {/* ── Status cell: LED + pill + always-rendered quick actions ── */}
                      <td style={{ ...tdBase, padding: "8px 12px" }}>
                        {hasVolunteerAssigned ? (
                          <button
                            type="button"
                            onClick={() => openStatusEditor(a)}
                            style={{
                              width: "100%",
                              border: `1px solid ${si.border}`,
                              background: si.bg,
                              color: si.color,
                              borderRadius: 12,
                              padding: "10px 12px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 8,
                              cursor: "pointer",
                              fontFamily: "inherit",
                              fontWeight: 700,
                              fontSize: 13,
                            }}
                            title="לחץ לשינוי סטטוס"
                          >
                            <StatusLed status={effectiveStatus} />
                            <span>{si.text}</span>
                          </button>
                        ) : (
                          <>
                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                              <StatusLed status={effectiveStatus} animated />
                              <span style={{
                                display: "inline-flex", alignItems: "center",
                                padding: "3px 10px", borderRadius: 20,
                                fontSize: 11, fontWeight: 600,
                                background: si.bg, color: si.color, whiteSpace: "nowrap",
                              }}>{si.text}</span>
                            </div>
                            <div className="act-quick-actions" style={{ display: "grid", gridTemplateColumns: "repeat(2,minmax(0,1fr))", gap: 6, marginTop: 6 }}>
                              <button className="act-quick-btn" onClick={() => setAssignModalItem(a)} style={{ ...quickBtnStyle, width: "100%", minHeight: 30, padding: "5px 8px", fontSize: 11 }}>שיוך</button>
                              <button className="act-quick-btn" onClick={() => markPending(a.id)} style={{ ...quickBtnStyle, width: "100%", minHeight: 30, padding: "5px 8px", fontSize: 11 }}>ממתין</button>
                              <button className="act-quick-btn" onClick={() => markNoResponse(a.id)} style={{ ...quickBtnStyle, width: "100%", minHeight: 30, padding: "5px 8px", fontSize: 11, gridColumn: "1 / -1" }}>אין מענה</button>
                            </div>
                          </>
                        )}
                      </td>

                      <td style={tdBase}>
                        <span style={{
                          display: "inline-flex", padding: "3px 10px", borderRadius: 20,
                          fontSize: 12, fontWeight: 500,
                          background: a.source === "auto" ? "#EDF4FF" : "#F5F5F7",
                          color: a.source === "auto" ? "#2563EB" : "#6B6B80",
                        }}>{a.source === "auto" ? "אוטומטי" : "ידני"}</span>
                      </td>

                      <td className="act-sticky-cell" style={{ ...tdBase, ...stickyActionCellStyle, whiteSpace: "nowrap", paddingInline: 10 }}>
                        <div className="act-row-actions" style={{ display: "flex", justifyContent: "center", gap: 4, opacity: 0.88, transition: "opacity .15s ease" }}>
                          <button onClick={() => openDuplicateModal(a)} title="שכפול" style={iconBtnStyle}><Ic.Copy /></button>
                          <button onClick={() => setEditItem(a)} title="עריכה" style={iconBtnStyle}><Ic.Edit /></button>
                          <button onClick={() => handleDelete(a.id)} title="מחיקה" style={{ ...iconBtnStyle, color: "#DC2626" }}><Ic.Trash /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {displayedActivities.length === 0 && (
                  <tr>
                    <td colSpan={11} style={{ textAlign: "center", padding: "52px 20px", color: "#A3A3B5" }}>
                      <div style={{ width: 56, height: 56, borderRadius: "50%", background: "#F5F5F7", display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 12, fontSize: 24 }}>📋</div>
                      <div style={{ fontSize: 15, fontWeight: 600, color: "#525264", marginBottom: 4 }}>אין פעילויות</div>
                      <div style={{ fontSize: 13 }}>לחץ על ״הזנת פעילות״ כדי להתחיל</div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        ) : (
          /* ════════ Mobile cards ════════ */
          <div style={{ padding: 12, display: "flex", flexDirection: "column", gap: 10 }}>
            {displayedActivities.map((a, idx) => {
              const effectiveStatus = getActivityStatus(a);
              const si = getStatus(effectiveStatus);
              const hasVolunteerAssigned = !!((a.volunteerId || "").trim());
              return (
                <div key={a.id} className="act-mobile-card" style={{
                  background: "#fff", border: "1px solid #E8E8ED",
                  borderRight: `4px solid ${si.border}`,
                  borderRadius: 14, padding: 14,
                  animation: `fadeInRow .25s ease ${Math.min(idx * 0.03, 0.5)}s both`,
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 15, color: "#1A1A2E" }}>
                        {volMap[a.volunteerId]?.name || "ללא מתנדב"}
                      </div>
                      <div style={{ fontSize: 12, color: "#6B7280", marginTop: 2 }}>
                        רכז: {coordinatorLabelForActivity(a)}
                      </div>
                      {benMap[a.beneficiaryId]?.name && (
                        <div style={{ fontSize: 13, color: "#8E8EA0", marginTop: 2 }}>נעזר: {benMap[a.beneficiaryId].name}</div>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: "#8E8EA0", direction: "ltr", textAlign: "left" }}>
                      {a.date || ""}<br />{a.time || ""}
                    </div>
                  </div>

                  <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center", marginBottom: 10, fontSize: 13, color: "#525264" }}>
                    {domMap[a.domainId] ? <DomainTag domain={domMap[a.domainId]} /> : "—"}
                    {!!(domMap[a.domainId]?.city || "").trim() && (
                      <span style={{ fontSize: 11, color: "#6B7280" }}>עיר: {a.city || domMap[a.domainId].city}</span>
                    )}
                    {!!(domMap[a.domainId]?.parentDomainId || "").trim() && (
                      <span style={{ fontSize: 11, color: "#6B7280" }}>
                        קטגוריית אב: {domMap[(domMap[a.domainId]?.parentDomainId || "").trim()]?.name || "—"}
                      </span>
                    )}
                    {a.subdomain && (<><span style={{ width: 1, height: 14, background: "#E8E8ED" }} /><span style={{ fontSize: 12 }}>{a.subdomain}</span></>)}
                    <span style={{ width: 1, height: 14, background: "#E8E8ED" }} />
                    <span>{Number(a.duration || 0)} שעות</span>
                    {getActivityContributionTotal(a) > 0 && (
                      <>
                        <span style={{ width: 1, height: 14, background: "#E8E8ED" }} />
                        <span style={{ color: "#9A3412", fontWeight: 700 }}>
                          {getActivityContributionTotal(a)} פריטים
                        </span>
                      </>
                    )}
                  </div>
                  {getActivityContributionTotal(a) > 0 && (
                    <div style={{ fontSize: 12, color: "#6B7280", marginBottom: 10 }}>
                      תרומות: {getActivityContributionSummary(a)}
                    </div>
                  )}
                  {getActivityContributionValueTotal(a) > 0 && (
                    <div style={{ fontSize: 12, color: "#0F766E", marginBottom: 10, fontWeight: 700 }}>
                      שווי כולל: {formatContributionCurrency(getActivityContributionValueTotal(a))}
                    </div>
                  )}

                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                    {hasVolunteerAssigned ? (
                      <button
                        type="button"
                        onClick={() => openStatusEditor(a)}
                        style={{
                          width: "100%",
                          border: `1px solid ${si.border}`,
                          background: si.bg,
                          color: si.color,
                          borderRadius: 12,
                          padding: "10px 12px",
                          fontSize: 13,
                          fontWeight: 700,
                          cursor: "pointer",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 8,
                          fontFamily: "inherit",
                        }}
                      >
                        <StatusLed status={effectiveStatus} />
                        {si.text}
                      </button>
                    ) : (
                      <>
                        <StatusLed status={effectiveStatus} animated />
                        <span style={{
                          display: "inline-flex", padding: "4px 12px", borderRadius: 20,
                          fontSize: 12, fontWeight: 600, background: si.bg, color: si.color,
                        }}>{si.text}</span>
                      </>
                    )}
                  </div>

                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {!hasVolunteerAssigned && (
                      <>
                        <button className="act-quick-btn" onClick={() => setAssignModalItem(a)} style={{ ...iconBtnStyle, width: 40, height: 40 }} title="שיוך למתנדב"><Ic.User /></button>
                        <button className="act-quick-btn" onClick={() => markPending(a.id)} style={{ ...iconBtnStyle, width: 40, height: 40 }} title="ממתין"><Ic.Clock /></button>
                        <button className="act-quick-btn" onClick={() => markNoResponse(a.id)} style={{ ...iconBtnStyle, width: 40, height: 40 }} title="אין מענה"><Ic.X /></button>
                      </>
                    )}
                    <button className="act-quick-btn" onClick={() => openDuplicateModal(a)} style={{ ...iconBtnStyle, width: 40, height: 40 }} title="שכפול">
                      <Ic.Copy />
                    </button>
                    <button className="act-quick-btn" onClick={() => setEditItem(a)} style={{ ...iconBtnStyle, width: 40, height: 40 }} title="עריכה">
                      <Ic.Edit />
                    </button>
                    <button className="act-quick-btn" onClick={() => handleDelete(a.id)} style={{ ...iconBtnStyle, width: 40, height: 40, color: "#DC2626", borderColor: "#FECACA" }} title="מחיקה">
                      <Ic.Trash />
                    </button>
                  </div>
                </div>
              );
            })}
            {displayedActivities.length === 0 && (
              <div style={{ textAlign: "center", padding: "48px 20px", color: "#A3A3B5" }}>
                <div style={{ width: 56, height: 56, borderRadius: "50%", background: "#F5F5F7", display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 12, fontSize: 24 }}>📋</div>
                <div style={{ fontSize: 15, fontWeight: 600, color: "#525264", marginBottom: 4 }}>אין פעילויות</div>
                <div style={{ fontSize: 13 }}>לחץ על ״הזנת פעילות״ כדי להתחיל</div>
              </div>
            )}
          </div>
        )}
      </div>
      {hasMoreActivities && (
        <div ref={listEndRef} style={{ display: "flex", justifyContent: "center", marginTop: 12 }}>
          <button
            style={btnSecondary}
            onClick={() => setVisibleCount((prev) => Math.min(prev + PAGE_SIZE, filteredActivities.length))}
          >
            טען עוד ({Math.min(PAGE_SIZE, filteredActivities.length - displayedActivities.length)})
          </button>
        </div>
      )}

      {isMobile && canAddActivities && (
        <div style={{ position: "fixed", left: 16, bottom: 84, zIndex: 30 }}>
          <button
            onClick={() => { setOpenedFromQuickAction(false); setAddDraft(null); setModal(true); }}
            style={{
              width: 58,
              height: 58,
              borderRadius: "50%",
              border: "none",
              background: "linear-gradient(180deg, #C41E3A, #A91A32)",
              color: "#fff",
              boxShadow: "0 14px 30px rgba(196,30,58,.3)",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
            title="הזנת פעילות"
          >
            <Ic.Plus />
          </button>
        </div>
      )}

      {/* ════════ Undo snackbar ════════ */}
      {lastDeleted && (
        <div style={{
          position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
          background: "#1A1A2E", color: "#fff", borderRadius: 14,
          padding: "10px 20px", display: "flex", alignItems: "center", gap: 12,
          fontSize: 14, fontWeight: 500, boxShadow: "0 8px 32px rgba(0,0,0,.18)",
          zIndex: 9999, animation: "slideUp .25s ease",
        }}>
          <span>הפעילות נמחקה</span>
          <button onClick={undoDelete} style={{
            background: "rgba(255,255,255,.15)", border: "none", color: "#fff",
            borderRadius: 8, padding: "5px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer",
          }}>ביטול</button>
          <button onClick={() => setLastDeleted(null)} style={{
            background: "transparent", border: "none", color: "#fff",
            borderRadius: 8, padding: "5px 8px", fontSize: 15, cursor: "pointer",
          }}>✕</button>
        </div>
      )}

      {/* ════════ Modals ════════ */}
      {modal && (
        <ActivityFormModal
          volunteers={volunteers} domains={domains} beneficiaries={beneficiaries}
          contributionTypes={contributionTypes}
          coordinators={users.filter((u) => u.role === "coordinator")}
          onCreateBeneficiary={async (form) => {
            const id = await addBeneficiary(form);
            showToast("נעזר חדש נוסף");
            refreshQuietly();
            return { id, ...form, active: form.active ?? true };
          }}
          initial={addDraft}
          title={addDraft?.beneficiaryId && !addDraft?.volunteerId && !addDraft?.date ? "הזנת פעילות" : (addDraft ? "שכפול פעילות" : "הזנת פעילות")}
          onClose={() => { setModal(false); setAddDraft(null); }} onSave={handleAdd}
          keepAdding={keepAdding} setKeepAdding={setKeepAdding}
          defaultDate={addInitialDate || ""}
          isMobile={isMobile}
        />
      )}
      {editItem && (
        <ActivityFormModal
          volunteers={volunteers} domains={domains} beneficiaries={beneficiaries}
          contributionTypes={contributionTypes}
          coordinators={users.filter((u) => u.role === "coordinator")}
          onCreateBeneficiary={async (form) => {
            const id = await addBeneficiary(form);
            showToast("נעזר חדש נוסף");
            refreshQuietly();
            return { id, ...form, active: form.active ?? true };
          }}
          initial={editItem} onClose={() => setEditItem(null)} onSave={handleEdit}
          title="עריכת פעילות"
          isMobile={isMobile}
        />
      )}
      {assignModalItem && (
        <Modal
          title="שיוך פעילות למתנדב"
          onClose={() => { setAssignModalItem(null); setAssignSearch(""); }}
          footer={
            <button onClick={() => { setAssignModalItem(null); setAssignSearch(""); }}
              style={{ ...btnSecondary, borderRadius: 12, padding: "9px 16px", fontSize: 13 }}>סגירה</button>
          }
        >
          <div style={{
            marginBottom: 12, padding: "10px 14px", background: "#FAFAFC",
            borderRadius: 10, fontSize: 13, color: "#525264",
            display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap",
          }}>
            <span>תחום: <b>{domMap[assignModalItem.domainId]?.name || "—"}</b></span>
            <span>תאריך: <b>{assignModalItem.date || "—"}</b></span>
            <span>שעה: <b>{assignModalItem.time || "—"}</b></span>
          </div>
          <input
            style={{ ...inputStyle, borderRadius: 12, marginBottom: 12, padding: "10px 14px", fontSize: 14 }}
            placeholder="🔍 חיפוש מתנדב לפי שם / טלפון / עיר"
            value={assignSearch}
            onChange={(e) => setAssignSearch(e.target.value)}
            autoFocus
          />
          <div style={{ maxHeight: 340, overflowY: "auto", border: "1px solid #E8E8ED", borderRadius: 12 }}>
            {filteredAssignVolunteers.map((v) => (
              <button key={v.id} type="button" className="act-assign-item"
                onClick={() => setMatchedVolunteer(assignModalItem.id, v.id)}
                style={{
                  width: "100%", textAlign: "right", border: "none",
                  borderBottom: "1px solid #F3F3F6", padding: "12px 14px",
                  background: "#fff", cursor: "pointer", display: "block",
                  transition: "background .1s",
                }}
              >
                <div style={{ fontWeight: 700, fontSize: 14, color: "#1A1A2E" }}>
                  {v.name || `${v.firstName || ""} ${v.lastName || ""}`.trim() || "ללא שם"}
                </div>
                <div style={{ fontSize: 12, color: "#8E8EA0", marginTop: 2 }}>
                  {v.phone || "—"} · {v.city || "—"}
                </div>
              </button>
            ))}
            {filteredAssignVolunteers.length === 0 && (
              <div style={{ padding: 20, textAlign: "center", color: "#A3A3B5" }}>לא נמצאו מתנדבים</div>
            )}
          </div>
        </Modal>
      )}
      {statusModalItem && (
        <Modal
          title="שינוי סטטוס פעילות"
          onClose={() => setStatusModalItem(null)}
          footer={
            <button
              onClick={() => setStatusModalItem(null)}
              style={{ ...btnSecondary, borderRadius: 12, padding: "9px 16px", fontSize: 13 }}
            >
              סגירה
            </button>
          }
        >
          <div style={{
            marginBottom: 12, padding: "10px 14px", background: "#FAFAFC",
            borderRadius: 10, fontSize: 13, color: "#525264",
            display: "flex", gap: 14, flexWrap: "wrap",
          }}>
            <span>מתנדב: <b>{volMap[statusModalItem.volunteerId]?.name || "—"}</b></span>
            <span>תחום: <b>{domMap[statusModalItem.domainId]?.name || "—"}</b></span>
            <span>תאריך: <b>{statusModalItem.date || "—"}</b></span>
          </div>
          <div style={{ display: "grid", gap: 8 }}>
            <button
              type="button"
              onClick={() => { setStatusModalItem(null); setAssignModalItem(statusModalItem); }}
              style={{ ...btnSecondary, borderRadius: 12, padding: "10px 14px", fontSize: 13, textAlign: "right" }}
            >
              שיוך למתנדב אחר
            </button>
            <button
              type="button"
              onClick={() => changeStatusFromModal(markPending, statusModalItem.id)}
              style={{ ...btnSecondary, borderRadius: 12, padding: "10px 14px", fontSize: 13, textAlign: "right", borderColor: "#FCD34D", color: "#B45309", background: "#FFFBEB" }}
            >
              סמן כממתין לשיוך
            </button>
            <button
              type="button"
              onClick={() => changeStatusFromModal(markNoResponse, statusModalItem.id)}
              style={{ ...btnSecondary, borderRadius: 12, padding: "10px 14px", fontSize: 13, textAlign: "right", borderColor: "#FECACA", color: "#B91C1C", background: "#FEF2F2" }}
            >
              סמן ללא מענה
            </button>
            <button
              type="button"
              onClick={() => openBranchActivityEditor(statusModalItem)}
              style={{ ...btnSecondary, borderRadius: 12, padding: "10px 14px", fontSize: 13, textAlign: "right", borderColor: "#CBD5E1", color: "#334155", background: "#F8FAFC" }}
            >
              סמן פעילות סניף
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════
   FORM MODAL
   ═══════════════════════════════════════════ */

const formInputStyle = {
  ...inputStyle,
  borderRadius: 10,
  padding: "9px 14px",
  fontSize: 14,
  transition: "border-color .15s, box-shadow .15s",
};

const formSelectStyle = {
  ...inputStyle,
  borderRadius: 10,
  padding: "9px 14px",
  fontSize: 14,
  cursor: "pointer",
  appearance: "none",
  backgroundImage: `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%238E8EA0' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
  backgroundRepeat: "no-repeat",
  backgroundPosition: "left 12px center",
  paddingLeft: 32,
};

const labelStyle = { display: "block", fontSize: 13, fontWeight: 600, color: "#525264", marginBottom: 6 };

export function ActivityFormModal({
  volunteers, domains, beneficiaries = [], coordinators = [], contributionTypes = [], onClose, onSave,
  initial = null, title = "הזנת פעילות", keepAdding = false, setKeepAdding = null, defaultDate = "",
  isMobile = false, onCreateBeneficiary = null,
}) {
  const isMainCategory = useCallback((d) => d?.isMainCategory !== false, []);
  const availableDomains = useMemo(
    () => domains.filter((d) => d.active !== false),
    [domains]
  );
  const domainById = useMemo(() => {
    const map = {};
    availableDomains.forEach((d) => { map[d.id] = d; });
    return map;
  }, [availableDomains]);
  const mainDomains = useMemo(
    () => availableDomains.filter((d) => isMainCategory(d)),
    [availableDomains, isMainCategory]
  );
  const childrenByMainId = useMemo(() => {
    const map = {};
    availableDomains.forEach((d) => {
      const parentId = (d.parentDomainId || "").trim();
      if (!parentId) return;
      if (!map[parentId]) map[parentId] = [];
      map[parentId].push(d);
    });
    return map;
  }, [availableDomains]);
  const resolveMainDomainId = useCallback((domainId) => {
    const current = domainById[domainId];
    if (!current) return "";
    const parentId = (current.parentDomainId || "").trim();
    if (!parentId) return current.id;
    return domainById[parentId] ? parentId : current.id;
  }, [domainById]);

  const initialDomainId = (initial?.domainId || "").trim();
  const defaultMainDomainId =
    resolveMainDomainId(initialDomainId) ||
    mainDomains[0]?.id ||
    availableDomains[0]?.id ||
    "";
  const defaultDomainId =
    initialDomainId ||
    defaultMainDomainId;
  const buildInitialForm = () => ({
    volunteerId: initial ? (initial?.volunteerId || "") : "",
    beneficiaryId: initial?.beneficiaryId || "",
    domainId: defaultDomainId,
    city: initial?.city || domainById[defaultDomainId]?.city || "",
    internalCoordinatorId: initial?.internalCoordinatorId || "",
    subdomain: initial?.subdomain || "",
    date: initial?.date || defaultDate || fmtDate(new Date()),
    time: initial?.time || "09:00",
    duration: initial?.duration ?? 1,
    contributions: normalizeActivityContributions(initial?.contributions),
    status: (initial?.volunteerId || "") ? "matched" : ((initial?.status === "matched" ? "pending" : initial?.status) || "pending"),
    notes: initial?.notes || "",
  });
  const [f, setF] = useState({ ...buildInitialForm() });
  const [mainDomainId, setMainDomainId] = useState(defaultMainDomainId);
  const [saving, setSaving] = useState(false);
  const [beneficiaryOptions, setBeneficiaryOptions] = useState(beneficiaries);
  const [quickBeneficiaryOpen, setQuickBeneficiaryOpen] = useState(false);
  const u = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const contributionRows = Array.isArray(f.contributions) ? f.contributions : [];
  const activeContributionTypes = useMemo(
    () => (contributionTypes || []).filter((item) => item.active !== false),
    [contributionTypes]
  );
  const topGridColumns = isMobile ? "1fr" : "1fr 1fr 1fr";
  const dateTimeGridColumns = isMobile ? "1fr" : "1fr 1fr";
  const cityOptions = useMemo(() => Array.from(new Set(
    [
      ...availableDomains.map((domain) => (domain?.city || "").trim()),
      ...volunteers.map((volunteer) => (volunteer?.city || "").trim()),
      ...beneficiaries.map((beneficiary) => (beneficiary?.city || "").trim()),
    ].filter(Boolean)
  )).sort((a, b) => a.localeCompare(b, "he")), [availableDomains, beneficiaries, volunteers]);
  const activeVolunteerOptions = useMemo(() => volunteers.filter((v) => v.status === "active"), [volunteers]);
  const activeBeneficiaryOptions = useMemo(() => beneficiaryOptions.filter((b) => b.active !== false), [beneficiaryOptions]);

  useEffect(() => {
    setMainDomainId(defaultMainDomainId);
    setF({ ...buildInitialForm() });
  }, [initial?.id, domains, defaultDate]);

  useEffect(() => {
    setBeneficiaryOptions(beneficiaries);
  }, [beneficiaries]);

  useEffect(() => {
    if (!mainDomainId) return;
    const exists = availableDomains.some((d) => d.id === mainDomainId && isMainCategory(d));
    if (!exists) {
      setMainDomainId(mainDomains[0]?.id || availableDomains[0]?.id || "");
    }
  }, [mainDomainId, mainDomains, availableDomains, isMainCategory]);

  const childDomainOptions = useMemo(
    () => childrenByMainId[mainDomainId] || [],
    [childrenByMainId, mainDomainId]
  );
  const uniqueChildDomainOptions = useMemo(() => {
    const seen = new Set();
    return childDomainOptions.filter((d) => {
      const key = (d.name || "").trim().toLowerCase();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [childDomainOptions]);
  const selectedChildId = uniqueChildDomainOptions.some((d) => d.id === f.domainId)
    ? f.domainId
    : (
      uniqueChildDomainOptions.find((d) => (d.name || "").trim() === (domainById[f.domainId]?.name || "").trim())?.id || ""
    );

  const submit = async () => {
    if ((f.status || "pending") === "none" && !(f.internalCoordinatorId || "").trim()) {
      setSaving(false);
      await showStyledAlert("בפעילות סניף יש לבחור רכז.");
      return;
    }
    setSaving(true);
    const shouldReset = await onSave(f, { keepOpen: !!setKeepAdding && keepAdding && !initial });
    if (shouldReset && !initial) {
      const reset = { ...buildInitialForm(), volunteerId: "", status: "pending", notes: "", internalCoordinatorId: "", contributions: [] };
      setF(reset);
      setMainDomainId(resolveMainDomainId(reset.domainId) || defaultMainDomainId);
    }
    setSaving(false);
  };

  return (
    <Modal title={title} onClose={onClose} footer={
      <>
        <button onClick={submit} disabled={saving} style={{
          ...btnPrimary, borderRadius: 12, padding: "9px 20px", fontSize: 14,
          fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 6,
        }}><Ic.Check /> {saving ? "שומר..." : "שמירה"}</button>
        <button onClick={onClose} style={{ ...btnSecondary, borderRadius: 12, padding: "9px 16px", fontSize: 13 }}>ביטול</button>
      </>
    }>
      {!initial && setKeepAdding && (
        <label style={{
          display: "inline-flex", alignItems: "center", gap: 8,
          fontSize: 13, color: "#525264", cursor: "pointer",
          padding: "8px 12px", borderRadius: 10, background: "#FAFAFC", marginBottom: 14,
        }}>
          <input type="checkbox" checked={keepAdding} onChange={(e) => setKeepAdding(e.target.checked)} style={{ accentColor: "#C41E3A" }} />
          השאר חלון פתוח להוספה נוספת
        </label>
      )}

      <div style={{ display: "grid", gridTemplateColumns: topGridColumns, gap: 14 }}>
        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>מתנדב</label>
          <SearchableSelect
            value={f.volunteerId}
            onChange={(nextVolunteerId) => {
              u("volunteerId", nextVolunteerId);
              if (nextVolunteerId) {
                u("status", "matched");
                u("internalCoordinatorId", "");
              } else if ((f.status || "pending") === "matched") {
                u("status", "pending");
              }
            }}
            options={activeVolunteerOptions}
            placeholder="ללא מתנדב"
            searchPlaceholder="חיפוש מתנדב לפי שם / טלפון / עיר"
            emptyText="לא נמצאו מתנדבים"
            getOptionValue={(option) => option.id}
            getOptionLabel={(option) => option.name || "ללא שם"}
            getOptionKeywords={(option) => [option.firstName, option.lastName, option.phone, option.city].filter(Boolean).join(" ")}
          />
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>נעזר</label>
          <SearchableSelect
            value={f.beneficiaryId}
            onChange={(nextBeneficiaryId) => u("beneficiaryId", nextBeneficiaryId)}
            options={activeBeneficiaryOptions}
            placeholder="ללא נעזר"
            searchPlaceholder="חיפוש נעזר לפי שם / טלפון / עיר"
            emptyText="לא נמצאו נעזרים"
            getOptionValue={(option) => option.id}
            getOptionLabel={(option) => option.name || "ללא שם"}
            getOptionKeywords={(option) => [option.phone, option.city, option.address, option.secondaryPhone, option.secondaryContactName].filter(Boolean).join(" ")}
          />
          {typeof onCreateBeneficiary === "function" && (
            <button type="button" style={{ ...btnSecondary, marginTop: 8, padding: "7px 12px", fontSize: 12 }} onClick={() => setQuickBeneficiaryOpen(true)}>
              <Ic.Plus /> הוסף נעזר חדש
            </button>
          )}
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>קטגוריה ראשית</label>
          <select
            style={formSelectStyle}
            value={mainDomainId}
            onChange={(e) => {
              const nextMainId = e.target.value;
              setMainDomainId(nextMainId);
              u("domainId", nextMainId);
              u("city", "");
              u("subdomain", "");
            }}
          >
            {mainDomains.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>תת קטגוריה</label>
        <select
          style={formSelectStyle}
          value={selectedChildId}
          onChange={(e) => u("domainId", e.target.value || mainDomainId)}
          disabled={!mainDomainId}
        >
          <option value="">ללא תת קטגוריה (ראשית בלבד)</option>
          {uniqueChildDomainOptions.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>עיר</label>
        <AutocompleteTextInput
          value={f.city || ""}
          onChange={(nextCity) => u("city", nextCity)}
          options={cityOptions}
          placeholder="לדוגמה: באר שבע"
          emptyText="אין ערים תואמות"
        />
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>פירוט נוסף (אופציונלי)</label>
        <input style={formInputStyle} value={f.subdomain || ""} onChange={(e) => u("subdomain", e.target.value)} placeholder="לדוגמה: סוג משימה / הערת שיוך" />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: dateTimeGridColumns, gap: 14 }}>
        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>תאריך</label>
          <input style={formInputStyle} type="date" value={f.date} onChange={(e) => u("date", e.target.value)} />
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>שעה</label>
          <input style={formInputStyle} type="time" value={f.time} onChange={(e) => u("time", e.target.value)} />
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>סטטוס מענה</label>
        <select
          style={formSelectStyle}
          value={f.status || "pending"}
          onChange={(e) => {
            const nextStatus = e.target.value;
            u("status", nextStatus);
            if (nextStatus !== "none") u("internalCoordinatorId", "");
          }}
          disabled={!!(f.volunteerId || "").trim()}
        >
          <option value="pending">ממתין לשיוך</option>
          <option value="matched">שויך מתנדב</option>
          <option value="no_response">לא היה מענה</option>
          <option value="none">פעילות סניף</option>
        </select>
        {!!(f.volunteerId || "").trim() && (
          <div style={{ marginTop: 6, fontSize: 12, color: "#6B7280" }}>
            כשמשויך מתנדב, הסטטוס נקבע אוטומטית ל״שויך מתנדב״.
          </div>
        )}
      </div>

      {(f.status || "pending") === "none" && (
        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>רכז סניף</label>
          <select
            style={formSelectStyle}
            value={f.internalCoordinatorId || ""}
            onChange={(e) => u("internalCoordinatorId", e.target.value)}
          >
            <option value="">בחר רכז</option>
            {coordinators.map((c) => (
              <option key={c.id} value={c.id}>{c.name || c.email || c.id}</option>
            ))}
          </select>
        </div>
      )}

      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>משך (שעות)</label>
        <select style={formSelectStyle} value={f.duration} onChange={(e) => u("duration", Number(e.target.value))}>
          {[0.5, 1, 1.5, 2, 2.5, 3, 4, 5, 6, 8].map((h) => <option key={h} value={h}>{h} שעות</option>)}
        </select>
      </div>

      <div style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 8 }}>
          <label style={{ ...labelStyle, marginBottom: 0 }}>תרומות</label>
          <button
            type="button"
            onClick={() => u("contributions", [...contributionRows, { type: "", quantity: 1, unitValue: "" }])}
            style={{ ...btnSecondary, borderRadius: 10, padding: "6px 12px", fontSize: 12 }}
          >
            <Ic.Plus /> הוסף תרומה
          </button>
        </div>
        {activeContributionTypes.length === 0 && (
          <div style={{ fontSize: 12, color: "#9A3412", marginBottom: 8 }}>
            אין עדיין סוגי תרומה מוגדרים. אפשר להגדיר אותם במסך "תרומות".
          </div>
        )}
        {contributionRows.length === 0 ? (
          <div style={{ fontSize: 12, color: "#6B7280", padding: "10px 12px", border: "1px dashed #D1D5DB", borderRadius: 12 }}>
            אין תרומות בפעילות הזו.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {contributionRows.map((item, index) => (
              <div key={`contribution-${index}`} style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "minmax(0,1fr) 140px 170px auto", gap: 10, alignItems: "center" }}>
                <select
                  style={formSelectStyle}
                  value={item?.type || ""}
                  onChange={(e) => {
                    const selectedType = activeContributionTypes.find((typeItem) => typeItem.name === e.target.value) || null;
                    const next = [...contributionRows];
                    next[index] = {
                      ...next[index],
                      type: e.target.value,
                      unitValue: selectedType?.unitValue ?? next[index]?.unitValue ?? "",
                    };
                    u("contributions", next);
                  }}
                >
                  <option value="">בחר סוג תרומה</option>
                  {activeContributionTypes.map((typeItem) => (
                    <option key={typeItem.id} value={typeItem.name}>{typeItem.name}</option>
                  ))}
                </select>
                <input
                  type="number"
                  min="1"
                  step="1"
                  style={formInputStyle}
                  value={item?.quantity ?? 1}
                  onChange={(e) => {
                    const next = [...contributionRows];
                    next[index] = { ...next[index], quantity: Number(e.target.value || 0) };
                    u("contributions", next);
                  }}
                  placeholder="כמות"
                />
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  style={formInputStyle}
                  value={item?.unitValue ?? ""}
                  onChange={(e) => {
                    const next = [...contributionRows];
                    next[index] = { ...next[index], unitValue: e.target.value };
                    u("contributions", next);
                  }}
                  placeholder='ערך ליחידה בש"ח'
                />
                <button
                  type="button"
                  onClick={() => {
                    const next = contributionRows.filter((_, itemIndex) => itemIndex !== index);
                    u("contributions", next);
                  }}
                  style={{ ...btnGhost, borderRadius: 10, padding: "9px 12px", color: "#DC2626" }}
                >
                  הסר
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>הערות</label>
        <textarea style={{ ...formInputStyle, resize: "vertical", minHeight: 64 }} value={f.notes} onChange={(e) => u("notes", e.target.value)} placeholder="הערות נוספות..." />
      </div>
      {quickBeneficiaryOpen && typeof onCreateBeneficiary === "function" && (
        <QuickBeneficiaryModal
          domains={domains}
          onClose={() => setQuickBeneficiaryOpen(false)}
          onSave={async (form) => {
            const created = await onCreateBeneficiary(form);
            if (created?.id) {
              setBeneficiaryOptions((prev) => {
                const next = [...prev.filter((item) => item.id !== created.id), created];
                next.sort((a, b) => (a.name || "").localeCompare(b.name || "", "he"));
                return next;
              });
              u("beneficiaryId", created.id);
            }
            setQuickBeneficiaryOpen(false);
          }}
        />
      )}
    </Modal>
  );
}

function QuickBeneficiaryModal({ domains, onClose, onSave }) {
  const activeDomains = domains.filter((d) => d.active !== false);
  const [form, setForm] = useState({
    name: "",
    phone: "",
    address: "",
    city: "",
    domain: activeDomains[0]?.id || domains[0]?.id || "",
    notes: "",
    volunteerIds: [],
    active: true,
  });
  const [saving, setSaving] = useState(false);
  const updateField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const submit = async () => {
    if (!(form.name || "").trim()) {
      await showStyledAlert("יש להזין שם נעזר.");
      return;
    }
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  return (
    <Modal
      title="הוספת נעזר חדש"
      onClose={onClose}
      footer={
        <>
          <button onClick={submit} disabled={saving} style={{ ...btnPrimary, borderRadius: 12, padding: "9px 18px" }}>{saving ? "שומר..." : "שמירה"}</button>
          <button onClick={onClose} style={{ ...btnSecondary, borderRadius: 12, padding: "9px 16px" }}>ביטול</button>
        </>
      }
    >
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div style={{ marginBottom: 16 }}><label style={labelStyle}>שם</label><input style={formInputStyle} value={form.name} onChange={(e) => updateField("name", e.target.value)} /></div>
        <div style={{ marginBottom: 16 }}><label style={labelStyle}>טלפון</label><input style={{ ...formInputStyle, direction: "ltr", textAlign: "right" }} value={form.phone} onChange={(e) => updateField("phone", e.target.value)} /></div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div style={{ marginBottom: 16 }}><label style={labelStyle}>כתובת</label><input style={formInputStyle} value={form.address} onChange={(e) => updateField("address", e.target.value)} /></div>
        <div style={{ marginBottom: 16 }}><label style={labelStyle}>עיר</label><input style={formInputStyle} value={form.city} onChange={(e) => updateField("city", e.target.value)} /></div>
      </div>
      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>תחום</label>
        <select style={formSelectStyle} value={form.domain} onChange={(e) => updateField("domain", e.target.value)}>
          {(activeDomains.length ? activeDomains : domains).map((domain) => <option key={domain.id} value={domain.id}>{domain.name}</option>)}
        </select>
      </div>
      <div>
        <label style={labelStyle}>הערות</label>
        <textarea style={{ ...formInputStyle, minHeight: 90, resize: "vertical" }} value={form.notes} onChange={(e) => updateField("notes", e.target.value)} />
      </div>
    </Modal>
  );
}
